import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Upload, FileText, XCircle, Cpu, Loader2 } from 'lucide-react';
import { slimZip, formatBytes } from '../../utils/slimZip';

// A zip is slimmed the moment it is picked (files the server would throw away
// are removed — see utils/slimZip), so what the page holds is what uploads.
// This remembers what each slimmed zip was before, so the list can show both sizes.
const slimNotes = new WeakMap();

const prepareFile = async (f) => {
  const result = await slimZip(f);
  if (result.changed) slimNotes.set(result.file, result);
  return result.file;
};

// The size of the file as it was picked, whether or not it was slimmed since.
const pickedSize = (f) => slimNotes.get(f)?.originalBytes ?? f.size;

const sizeLabel = (f) => {
  const note = slimNotes.get(f);
  if (!note) return formatBytes(f.size);
  // Worded to be true on every page: what is left out may be build output, git
  // history, or (in a document pack) pictures.
  return `${formatBytes(note.originalBytes)} → ${formatBytes(f.size)} · ${note.skipped.toLocaleString()} `
    + `${note.skipped === 1 ? 'file the AI does not read was' : 'files the AI does not read were'} left out`;
};

/**
 * File upload tab content with drag-and-drop support.
 *
 * Single-file mode (default): `file` is a File (or null) and `setFile` is
 * called with a File / null — unchanged behaviour for existing callers.
 *
 * Multi-file mode (`multiple`): `file` is an ARRAY of Files and `setFile` is
 * always called with an array. Dropping or browsing appends to the selection,
 * and each entry gets its own remove button.
 *
 * In both modes a picked .zip is slimmed first, so the File handed to `setFile`
 * is the smaller one that will actually be uploaded (same filename).
 *
 * Optional `children` are rendered between the info box and the submit button
 * (used by the multi-source upload to host its repository section).
 */
const FileUploadTab = ({
  file,
  setFile,
  onUpload,
  disabled,
  accept = '.zip,.cs,.py,.sql,.xml,.json,.js,.ts,.config,.csproj,.sln,.txt,.md,.yaml,.yml',
  hint = 'ZIP, C#, Python, SQL, XML, JSON, Config files (max 100MB)',
  multiple = false,
  children,
}) => {
  // Normalise the incoming value in multiple mode (tolerates null / a bare File)
  const files = multiple ? (Array.isArray(file) ? file : file ? [file] : []) : [];
  const hasFiles = multiple ? files.length > 0 : !!file;
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

  // How many picks are still being slimmed — the submit button waits for them
  const [preparing, setPreparing] = useState(0);
  // The newest selection, to append to once slimming (which takes a moment) is done
  const latestFiles = useRef(files);
  latestFiles.current = files;

  // Slim any zips, then append, skipping duplicates (same name + size as picked)
  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).slice(0, multiple ? undefined : 1);
    if (incoming.length === 0) return;
    setPreparing((n) => n + 1);
    let ready = incoming; // if slimming ever fails, the files go up as they were picked
    try {
      ready = await Promise.all(incoming.map(prepareFile));
    } catch (err) {
      console.error('Could not slim the selected files:', err);
    } finally {
      setPreparing((n) => n - 1);
    }
    if (!multiple) {
      setFile(ready[0]);
      return;
    }
    const merged = [...latestFiles.current];
    ready.forEach((f) => {
      if (!merged.some((m) => m.name === f.name && pickedSize(m) === pickedSize(f))) merged.push(f);
    });
    latestFiles.current = merged;
    setFile(merged);
  };

  const removeFileAt = (index) => setFile(files.filter((_, i) => i !== index));

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              hasFiles ? 'border-[#2B8FD8] bg-blue-50/50' : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(e.dataTransfer.files);
            }}
          >
            {!multiple && file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-[#2B8FD8]" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">{sizeLabel(file)}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-1">
                  {multiple ? 'Drag & drop your files here, or' : 'Drag & drop your file here, or'}
                </p>
                <label className="cursor-pointer">
                  <span className="text-[#2B8FD8] font-medium hover:underline">browse files</span>
                  <input
                    type="file"
                    className="hidden"
                    accept={accept}
                    {...(multiple ? { multiple: true } : {})}
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = ''; // allow re-selecting the same file after a remove
                    }}
                    data-testid="file-input"
                  />
                </label>
                <p className="text-xs text-slate-400 mt-2">
                  {hint}
                </p>
              </>
            )}
          </div>

          {preparing > 0 && (
            <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="preparing-files">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking your files for anything that does not need uploading...
            </p>
          )}

          {multiple && files.length > 0 && (
            <div className="space-y-2" data-testid="selected-files-list">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">
                  {files.length} file{files.length === 1 ? '' : 's'} selected
                  <span className="text-slate-400 font-normal"> · {formatBytes(totalBytes)} to upload</span>
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setFile([])}
                  data-testid="clear-files-btn"
                >
                  Clear all
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${f.size}-${i}`}
                    className="flex items-center gap-3 px-3 py-2"
                    data-testid="selected-file-row"
                  >
                    <FileText className="h-4 w-4 text-[#2B8FD8] shrink-0" />
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                      <p className="text-xs text-slate-500">{sizeLabel(f)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFileAt(i)}
                      data-testid={`remove-file-${i}`}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Smart File Filtering</h4>
            <p className="text-xs text-slate-500">
              The AI automatically skips: assembly files, NuGet packages, bin/obj folders,
              node_modules, images, and other non-source files. Only your actual integration
              code, SQL, models, and config files are analyzed. The skipped files are taken
              out of a zip before it is uploaded, so a zip that is mostly build output sends
              in seconds.
            </p>
          </div>

          {children}

          <Button
            className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
            onClick={onUpload}
            disabled={disabled || preparing > 0}
            data-testid="generate-docs-btn"
          >
            <Cpu className="h-5 w-5 mr-2" />
            Generate Documentation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileUploadTab;
